// V·IAnet Governance — AdminCatalogoSimple
// Patron reusable de CRUD simple (codigo, nombre, descripcion, activo).
// Usado por AdminTiposContrato y AdminCategoriasLaborales.
// Patron canonico (0473 + 0479/0479.1a/0479.1b): Codigo | Nombre | Descripcion | Activo | Acciones (Edit · Power · Trash)

import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../../../../i18n';
import { C } from '../../../../../lib/design';
import { ConfirmDialog } from '../../../../shared/components/ConfirmDialog';
import {
  AdminPageHeader,
  AdminTableShell,
  AdminActionButton,
  type AdminTableColumn,
} from '../../../../shared/components/admin';

export interface CatalogoSimpleItem {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  activo: boolean;
}

export interface CatalogoSimpleLabels {
  title: string;
  newItem: string;
  editItem: string;
  columns: {
    codigo: string;
    nombre: string;
    descripcion: string;
    activo: string;
    acciones: string;
  };
  placeholders: { nombre: string; descripcion: string };
  confirmDeactivate: string;
  enUso: string;
  itemLabel: string; // ej: 'tipos de contrato', 'categorias laborales'
}

export interface CatalogoSimpleApi {
  list: () => Promise<{ data: CatalogoSimpleItem[] }>;
  create: (body: {
    nombre: string;
    descripcion?: string;
    activo?: boolean;
  }) => Promise<{ data: CatalogoSimpleItem }>;
  update: (id: string, body: Record<string, unknown>) => Promise<{ data: CatalogoSimpleItem }>;
  remove: (id: string) => Promise<{ data: CatalogoSimpleItem }>;
}

function EditIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke={C.azul2}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#DC2626"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    </svg>
  );
}

function PowerIcon({ activo }: { activo: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke={activo ? '#16A34A' : '#9CA3AF'}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2v10" />
      <path d="M18.4 6.6a9 9 0 1 1-12.77.04" />
    </svg>
  );
}

function ActivoBadge({
  activo,
  labelOn,
  labelOff,
}: {
  activo: boolean;
  labelOn: string;
  labelOff: string;
}) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
        background: activo ? '#ECFDF5' : '#FEF2F2',
        color: activo ? '#16A34A' : '#DC2626',
      }}
    >
      {activo ? labelOn : labelOff}
    </span>
  );
}

export function AdminCatalogoSimple({
  labels,
  api,
}: {
  labels: CatalogoSimpleLabels;
  api: CatalogoSimpleApi;
}) {
  const { t } = useI18n();
  const adm = t.structure.admin;
  const [items, setItems] = useState<CatalogoSimpleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  const [modal, setModal] = useState<CatalogoSimpleItem | 'new' | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [toast, setToast] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api
      .list()
      .then((r) => setItems(r.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.remove(id);
      load();
      showToast(adm.saved);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error';
      showToast(msg);
    } finally {
      setConfirmId(null);
    }
  };

  const handleToggleActivo = async (r: CatalogoSimpleItem) => {
    try {
      await api.update(r.id, { activo: !r.activo });
      load();
      showToast(r.activo ? adm.saved : adm.saved);
    } catch {
      showToast('Error');
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = items
    .filter((it) => (mostrarInactivos ? true : it.activo !== false))
    .filter((it) =>
      q
        ? it.codigo.toLowerCase().includes(q) ||
          it.nombre.toLowerCase().includes(q) ||
          (it.descripcion ?? '').toLowerCase().includes(q)
        : true,
    );

  const columns: AdminTableColumn<CatalogoSimpleItem>[] = [
    {
      key: 'codigo',
      label: labels.columns.codigo,
      sortable: true,
      sortKey: (r) => r.codigo,
      render: (r) => (
        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: C.azul2 }}>
          {r.codigo}
        </span>
      ),
    },
    {
      key: 'nombre',
      label: labels.columns.nombre,
      sortable: true,
      sortKey: (r) => r.nombre,
      render: (r) => <span style={{ fontWeight: 500 }}>{r.nombre}</span>,
    },
    {
      key: 'descripcion',
      label: labels.columns.descripcion,
      render: (r) => <span style={{ color: '#6B7280', fontSize: 12 }}>{r.descripcion ?? '—'}</span>,
    },
    {
      key: 'activo',
      label: labels.columns.activo,
      render: (r) => (
        <ActivoBadge
          activo={r.activo}
          labelOn={t.common.estados.activo}
          labelOff={t.common.estados.inactivo}
        />
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title={labels.title}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: adm.search,
        }}
        onNew={{
          label: labels.newItem,
          onClick: () => setModal('new'),
        }}
        extras={
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: '#5A6171',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={mostrarInactivos}
              onChange={(e) => setMostrarInactivos(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            {adm.mostrarInactivos}
          </label>
        }
      />

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 200,
            background: C.azul1,
            color: '#fff',
            padding: '10px 20px',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast}
        </div>
      )}

      <AdminTableShell
        columns={columns}
        rows={filtered}
        rowKey={(r) => r.id}
        onRowClick={(r) => setModal(r)}
        loading={loading}
        loadingMessage={t.common.cargando ?? 'Cargando…'}
        emptyMessage={adm.noResults}
        actionsLabel={labels.columns.acciones}
        actions={(r) => (
          <>
            <AdminActionButton
              icon={<EditIcon />}
              label={labels.editItem}
              onClick={() => setModal(r)}
            />
            <AdminActionButton
              icon={<PowerIcon activo={r.activo} />}
              label={r.activo ? adm.tooltips.toggleDesactivar : adm.tooltips.toggleActivar}
              onClick={() => handleToggleActivo(r)}
            />
            <AdminActionButton
              icon={<TrashIcon />}
              label={labels.confirmDeactivate}
              variant="danger"
              onClick={() => setConfirmId(r.id)}
            />
          </>
        )}
      />

      <div
        style={{
          padding: '8px 16px',
          fontSize: 12,
          color: '#6B7280',
          borderTop: '1px solid #E5E7EB',
        }}
      >
        {filtered.length} de {items.length} {labels.itemLabel}
      </div>

      {modal !== null && (
        <CatalogoSimpleModal
          item={modal === 'new' ? null : modal}
          labels={labels}
          api={api}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            load();
            showToast(adm.saved);
          }}
        />
      )}

      <ConfirmDialog
        open={confirmId !== null}
        title={labels.confirmDeactivate}
        message={labels.confirmDeactivate}
        danger
        onCancel={() => setConfirmId(null)}
        onConfirm={() => confirmId && handleDelete(confirmId)}
      />
    </div>
  );
}

function CatalogoSimpleModal({
  item,
  labels,
  api,
  onClose,
  onSaved,
}: {
  item: CatalogoSimpleItem | null;
  labels: CatalogoSimpleLabels;
  api: CatalogoSimpleApi;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const isEdit = item !== null;
  const [form, setForm] = useState({
    nombre: item?.nombre ?? '',
    descripcion: item?.descripcion ?? '',
    activo: item?.activo ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!form.nombre.trim()) {
      setError(t.people.validation.required);
      return;
    }
    try {
      setSaving(true);
      if (isEdit) await api.update(item.id, form);
      else
        await api.create({
          nombre: form.nombre,
          descripcion: form.descripcion || undefined,
          activo: form.activo,
        });
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const inp: React.CSSProperties = {
    padding: '8px 10px',
    border: '1px solid #D1D5DB',
    borderRadius: 6,
    fontSize: 13,
    width: '100%',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  };
  const lbl: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: '#5A6171',
    marginBottom: 4,
    display: 'block',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 12,
          borderTop: `3px solid ${C.azul1}`,
          maxWidth: 500,
          width: '95%',
          padding: '24px 28px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <h2 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: C.azul1 }}>
          {isEdit ? labels.editItem : labels.newItem}
        </h2>
        {isEdit && item?.codigo && (
          <div
            style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 12,
              color: C.azul2,
              marginBottom: 16,
            }}
          >
            {item.codigo}
          </div>
        )}
        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={lbl}>{labels.columns.nombre} *</label>
            <input
              style={inp}
              value={form.nombre}
              onChange={(e) => {
                setForm((p) => ({ ...p, nombre: e.target.value }));
                setError('');
              }}
              placeholder={labels.placeholders.nombre}
            />
          </div>
          <div>
            <label style={lbl}>{labels.columns.descripcion}</label>
            <textarea
              style={{ ...inp, resize: 'vertical', minHeight: 60 }}
              value={form.descripcion}
              onChange={(e) => setForm((p) => ({ ...p, descripcion: e.target.value }))}
              placeholder={labels.placeholders.descripcion}
              rows={3}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={form.activo}
              onChange={(e) => setForm((p) => ({ ...p, activo: e.target.checked }))}
            />
            {t.common.estados.activo}
          </label>
        </div>
        {error && (
          <div
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              marginTop: 12,
              background: '#FEF2F2',
              color: '#DC2626',
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              background: 'transparent',
              border: '1px solid #D1D5DB',
              borderRadius: 6,
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
              color: '#374151',
            }}
          >
            {t.common.cancelar}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 24px',
              background: C.azul2,
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
              fontFamily: 'inherit',
            }}
          >
            {saving ? t.common.guardando : t.common.guardar}
          </button>
        </div>
      </div>
    </div>
  );
}
